/* eslint-disable react/jsx-one-expression-per-line */
/* eslint-disable max-len */
/* eslint-disable @typescript-eslint/no-unused-vars */
import React, { useEffect, useRef, useState } from 'react';

import './React/Style/App.css';
import 'react-toastify/dist/ReactToastify.css';

import { ToastContainer } from 'react-toastify';

import { Config } from './Resources/Config';

import { CatalogFileFilter, CatalogFileRoot } from './common/libraries/fileFormats/catalog/catalog';
import { CustomizationConfiguration } from './common/libraries/modelViewer/Data/CustomizationConfiguration';
import { getSLTXFileContentsInLatestVersion } from './common/libraries/fileFormats/sltx/sltxVersion';
import { FilePaths } from './common/libraries/fileSystem/filePaths';

import { useAppDispatch, useAppSelector } from './Redux/Store/hooks';

import { NavigationReducer, NavigationPage } from './Redux/Reducers/NavigationReducer';
import { EnvironmentSettingsReducer } from './Redux/Reducers/EnvironmentSettingsReducer';
import { TextsReducer } from './Redux/Reducers/TextsReducer';

import { PickedFilesHandler } from './React/Util/PickedFilesHandler';
import { FileHandlerService } from './React/Services/FileHandlerService';
import { POSCatalogData, POSCatalogModelData } from './common/libraries/modelViewer/Data/POSCatalogData';
import { CollectionController } from './React/Pages/Controllers/CollectionController';
import { SearchController } from './React/Pages/Controllers/SearchController';
import { HomeController } from './React/Pages/Controllers/HomeController';
import { OrderController } from './React/Pages/Controllers/OrderController';
import { ObjectController } from './React/Pages/Controllers/ObjectController';
import { HeaderComponent } from './React/Pages/Components/Specific/HeaderComponent';
import { FooterComponent } from './React/Pages/Components/Specific/FooterComponent';
import { getEnGbStrings } from './Resources/Strings/EnGb';
import { getPtPtStrings } from './Resources/Strings/PtPt';
import { Strings } from './Resources/Strings/Strings';

import { FileUtil } from './React/Util/FileUtil';
import { LoginView } from './React/Pages/Views/LoginView';
import { ProfileReducer } from './Redux/Reducers/ProfileReducer';
import { CustomAttributeData } from './common/libraries/customAttributes/customAttributes';
import { getFrFrStrings } from './Resources/Strings/FrFr';
import { getEsEsStrings } from './Resources/Strings/EsEs';
import { getDeGeStrings } from './Resources/Strings/GeGe';
import { OrderSessionData } from './common/libraries/fileFormats/catalogOrder/catalogOrder';
import { getStaticTranslation, getTranslatedStrings } from './Resources/Strings/StaticTagsTranslation';
import { decryptData } from './common/libraries/react/utils/Crypto';
import { SLTXFile } from './common/libraries/fileFormats/sltx/sltxFile';

type AppParameters = {
  configurations : Config,
  clearCacheHandler : () => void;
};

export function App(
  {
    configurations,
    clearCacheHandler,
  } : AppParameters,
) : JSX.Element {

  const [orderSessionData, setOrderSessionData] = useState<OrderSessionData>({
    customerId: '',
    customerEmailAccount: '',
    nonce: '',
  });

  const [nonce, setNonce] = useState<string>('');

  const [loginOk, setLoginOk] = useState<boolean>(false);

  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const aNonce = urlParams.get('_id') || '';
    if (aNonce) setNonce(aNonce);

    // We only care for login if we're in a website
    if (configurations.APPLICATION_TYPE !== 'electron') {
      setLoginOk(false);
    } else {
      setLoginOk(true);
    }

  }, []);

  // Grab the dispatch hook from Redux
  const dispatch = useAppDispatch();

  // Create class to manage picked files
  const refPickedFilesHandler = useRef<PickedFilesHandler>(new PickedFilesHandler());

  // Grab the actions from Reducers
  const profileActions = ProfileReducer.actions;
  const navigationActions = NavigationReducer.actions;
  const textsActions = TextsReducer.actions;
  const environmentSettingsActions = EnvironmentSettingsReducer.actions;

  // From the store, get the page to render
  const page = useAppSelector((state) => {
    return state.NavigationReducer.page;
  });

  // From the store, get the environment settings
  const environmentSettings = useAppSelector((state) => {
    return state.EnvironmentSettingsReducer;
  });

  const [theme, setTheme] = useState<string>('light-theme');

  // The catalog object
  const [posCatalogData, setPOSCatalogData] = useState<POSCatalogData | undefined>();

  // All the filters that are collected from the catalog objects
  const [filters, setFilters] = useState<CatalogFileFilter[]>([]);

  // Error message to indicate issues with loading the catalog
  const [errorMessage, setErrorMessage] = useState<string>('');

  const [showLoginView, setShowLoginView] = useState<boolean>(false);

  const changeTheme = () : void => {
    if (theme === 'light-theme') setTheme('dark-theme');
    else setTheme('light-theme');
  };

  const loadFileContents = (path : string) => {
    return FileHandlerService.LoadSync(path);
  };

  const loadCatalog = async () : Promise<POSCatalogData> => {

    // Create the entity that will hold all the data (catalog + models data)

    const newPOSCatalogData = new POSCatalogData();

    // Load the catalog

    let catalogFileContents : CatalogFileRoot | undefined;

    try {
      catalogFileContents = await loadFileContents(
        FilePaths.join(configurations.CATALOG_INFO_PATH, configurations.CATALOG_FILE_NAME),
      );
    } catch (err) {
      // eslint-disable-next-line no-console
      console.log(`Problem while loading the catalog: ${err}`);

      throw new Error('Catalog not loaded. Do you have a catalog installed?');
    }

    if (catalogFileContents === undefined) {
      throw new Error('Catalog not loaded. Do you have a catalog installed?');
    }

    // Save the catalog file information in the posCatalogData

    newPOSCatalogData.catalogFileInfo = catalogFileContents;

    // Load the information fof each model mentioned in the catalog

    // eslint-disable-next-line no-restricted-syntax
    for (const collection of catalogFileContents.collections) {

      // eslint-disable-next-line no-restricted-syntax
      for (const catalogObject of collection.objects) {

        // Get the key of this model
        const catalogModelKey = catalogObject.versionControl.key;

        // Build a path to the catalog object file

        const catalogObjectFilePath : string = FilePaths.join(
          configurations.CATALOG_INFO_PATH,
          catalogObject.infoRef,
        );

        // Load catalog object
        // eslint-disable-next-line no-await-in-loop
        const modelFileContents = await loadFileContents(
          catalogObjectFilePath,
        );

        // const catalogObjectExtraInfo = getSLTXFileContentsInLatestVersion(modelFileContents);
        // eslint-disable-next-line no-await-in-loop
        const catalogObjectExtraInfo = await SLTXFile.LoadModelInfoFile(catalogObjectFilePath);

        // Load catalog object customizations

        let modelCustomization : CustomizationConfiguration | undefined;

        const catalogCollectionInfo = catalogFileContents.collections.find((c) => c.id === collection.id);
        const catalogObjectInfo = catalogCollectionInfo?.objects.find((o) => o.id === catalogObject.id);

        // Create a new data entity to save the information about the model
        const newPOSModelData = new POSCatalogModelData();
        newPOSModelData.data = catalogObjectExtraInfo;
        newPOSModelData.customizations = modelCustomization;
        newPOSModelData.catalogData = catalogObject;

        if (catalogObjectInfo?.rules) {
          newPOSModelData.catalogData.rules = catalogObjectInfo?.rules;
        }

        // Save this model information in the POSCatalogData instance

        newPOSCatalogData.modelsData.set(catalogModelKey, newPOSModelData);
        // eslint-disable-next-line max-len
        dispatch(textsActions.setTranslation({ translations: catalogFileContents.tagsTranslation }));
      }
    }

    return newPOSCatalogData;
  };

  // Callback to Authentication
  const handleAuthentication = (sessionToken : string) => {
    dispatch(profileActions.setSessionToken({ sessionToken }));
  };

  const changeLanguage = (language : string) => {
    let strings : Strings | undefined;

    switch (language) {
      case 'DE': {
        strings = getDeGeStrings();
        break;
      }
      case 'FR': {
        strings = getFrFrStrings();
        break;
      }
      case 'ES': {
        strings = getEsEsStrings();
        break;
      }
      case 'PT': {
        strings = getPtPtStrings();
        break;
      }
      case 'EN': {
        strings = getEnGbStrings();
        break;
      }
      default: {
        strings = getPtPtStrings();
        break;
      }
    }

    const tags = getStaticTranslation(posCatalogData?.catalogFileInfo?.tagsTranslation);
    if (strings !== undefined && tags.length > 0) strings = getTranslatedStrings(language.toLowerCase(), strings, tags);

    const itemsAttributeData : CustomAttributeData[] = [];
    posCatalogData?.catalogFileInfo?.itemsCustomAttributesDefinitions.attributes.forEach((att) => {
      const attribute = att;
      const translatedName = att.translations?.find((t) => t.lang === language.toLowerCase())?.value;
      attribute.name = translatedName !== undefined ? translatedName : att.name;
      attribute.selectedLang = language.toLowerCase();
      itemsAttributeData.push(attribute);
    });

    const orderAttributeData : CustomAttributeData[] = [];
    posCatalogData?.catalogFileInfo?.orderCustomAttributesDefinitions.attributes.forEach((att) => {
      const attribute = att;
      const translatedName = att.translations?.find((t) => t.lang === language.toLowerCase())?.value;
      attribute.name = translatedName !== undefined ? translatedName : att.name;
      orderAttributeData.push(attribute);
    });

    if (posCatalogData && posCatalogData.catalogFileInfo) {
      posCatalogData.catalogFileInfo.itemsCustomAttributesDefinitions.attributes = itemsAttributeData;
      posCatalogData.catalogFileInfo.orderCustomAttributesDefinitions.attributes = orderAttributeData;
      setPOSCatalogData(posCatalogData);
    }

    dispatch(textsActions.setTexts({ strings, language }));
  };

  const loadFiles = async () => {

    // Load the catalog
    const loadedCatalog = await loadCatalog();

    // Save the catalog data
    setPOSCatalogData(loadedCatalog);

    if (loadedCatalog.catalogFileInfo) {
      setFilters(loadedCatalog.catalogFileInfo.filters);
    }
  };

  useEffect(() => {

    // eslint-disable-next-line no-console
    console.log('Starting MindSales POS!');
    // eslint-disable-next-line no-console
    console.log(
      `  Environment Settings: ${JSON.stringify(configurations)}`,
    );

    // Grab the configurations and save them to the store

    dispatch(environmentSettingsActions.setConfiguration(configurations));
    dispatch(textsActions.setTexts({ strings: getPtPtStrings(), language: 'PT' }));

    // Load the required files
    loadFiles()
      .then(() => {
        // All files were loaded.
        // Navigate to home page

        dispatch(
          navigationActions.changeState({
            page: NavigationPage.Home,
            collection: '',
            object: '',
            filter: undefined,
            searchQuery: undefined,
          }),
        );
      })
      .catch((error) => {
        // Something was wrong with the required files
        // Indicate this to the user
        setErrorMessage(error.message);

        // eslint-disable-next-line no-console
        console.log(JSON.stringify(error));
      });
  }, []);

  // Read the query parameters loging information
  useEffect(() => {
    const encryptedData = nonce;
    const keyHex = '36c554c675442db981595b5383599337';
    const ivHex = '176f77929969108ca16f853cb4b41015';

    if (encryptedData) {
      decryptData(encryptedData, keyHex, ivHex)
        .then((decryptedData) => {
          console.log(`Encrypted data: ${encryptedData}`);
          console.log('Decrypted remote data:', decryptedData);

          const { eid, cid, tid } = (decryptedData);
          // Validate timestamp
          const time = (new Date().getTime() / 1000);
          if (time - tid > (60 * 60 * 12)) { // 12 hour expiration expiration
            setLoginOk(false);
          } else {
            setLoginOk(true);
            if (!cid) {
              console.log(`Setting DEMO mode => eid=${decodeURIComponent(eid)}`);
              setOrderSessionData({ customerId: '', customerEmailAccount: '', nonce });
            } else {
              console.log(`Setting PRODUCTION mode => eid=${decodeURIComponent(eid)} and cid=${decodeURIComponent(cid)}`);
              setOrderSessionData({ customerId: decodeURIComponent(cid), customerEmailAccount: decodeURIComponent(eid), nonce });
            }
          }
        }).catch((error) => setLoginOk(false));
    }
  }, [nonce]);

  useEffect(() => {
    changeLanguage('PT');
  }, [posCatalogData]);

  // From the store, get the strings
  const strings = useAppSelector((state) => {
    return state.TextsReducer.strings;
  })!;

  const renderPage = () => {
    if (!loginOk) {
      return (
        <div className="error">
          <h1>{strings ? strings.SESSION_EXPIRED : 'Expired Session'} </h1>
        </div>
      );
    }
    switch (page) {

      case NavigationPage.Home:
      {
        if (posCatalogData === undefined
          || posCatalogData.catalogFileInfo === undefined) {
          return <div className="empty" />;
        }

        return (
          <HomeController
            catalog={posCatalogData}
            collections={posCatalogData.catalogFileInfo.collections}
            spotlight_collections={posCatalogData.catalogFileInfo.spotlightCollections}
            spotlight_objects={posCatalogData.catalogFileInfo.spotlightObjects}
            clearCacheHandler={clearCacheHandler}
          />
        );
      }
      case NavigationPage.Order:
      {
        if (posCatalogData === undefined) {
          return <div className="empty" />;
        }

        return (
          <OrderController
            catalog={posCatalogData}
            refPickedFilesHandler={refPickedFilesHandler}
            orderSessionData={orderSessionData}
          />
        );
      }

      case NavigationPage.Collection:
      {
        if (posCatalogData === undefined
          || posCatalogData.catalogFileInfo === undefined) {
          return <div className="empty" />;
        }

        return (
          <CollectionController
            catalog={posCatalogData}
            catalogCollections={posCatalogData.catalogFileInfo.collections}
          />
        );
      }

      case NavigationPage.Object:
      {
        if (posCatalogData === undefined
          || posCatalogData.catalogFileInfo === undefined) {
          return <div className="empty" />;
        }

        return (
          <ObjectController
            catalog={posCatalogData}
            collections={posCatalogData.catalogFileInfo.collections}
            configurations={configurations}
          />
        );
      }

      case NavigationPage.Search:
      {
        if (posCatalogData === undefined) {
          return <div className="empty" />;
        }

        return <SearchController catalog={posCatalogData} />;
      }

      default:
        return (
          <div style={{ marginLeft: '100px', fontSize: '10pt' }}>
            <p>
              Loading the catalog from
              {' '}
              {environmentSettings.catalogInfoPath
                  + environmentSettings.catalogFileName}
              .
            </p>
            {errorMessage === '' && <p>Please wait...</p>}
            {errorMessage !== '' && (
            <div>
              <p style={{ color: 'rgb(255, 128, 128)' }}>{errorMessage}</p>
              <p>Please contact Mind support</p>
            </div>
            )}
          </div>
        );
    }
  };

  const renderPopUps = () => {
    if (showLoginView) {
      if (posCatalogData === undefined || posCatalogData.catalogFileInfo === undefined) {
        return null;
      }

      return (
        <LoginView
          email={posCatalogData.catalogFileInfo.sourceData.channelEmail}
          channelName={posCatalogData.catalogFileInfo.sourceData.channelName}
          catalogKey={posCatalogData.catalogFileInfo.sourceData.catalogKey}
          backendUrl={posCatalogData.catalogFileInfo.sourceData.channelBackendInsertOrderEndpoint}
          showLogin={setShowLoginView}
          authenticator={handleAuthentication}
        />
      );
    }

    return null;
  };

  return (
    <div className={theme}>
      <ToastContainer
        position="top-right"
        autoClose={1}
        hideProgressBar={false}
        newestOnTop={true}
        closeOnClick={true}
        rtl={false}
        draggable
        pauseOnHover={false}
        theme="light"
      />
      <div className="container-fluid appbody">
        {configurations.APPLICATION_TYPE !== 'electron' && orderSessionData && !orderSessionData.customerEmailAccount && <div><h1 style={{ color: '#FF0000', backgroundColor: '#CCCCCCCC' }}>{strings ? strings.DEMO_WARNING : 'For demonstration purposes only!'}</h1></div>}
        {posCatalogData && posCatalogData.catalogFileInfo && filters && (
        <HeaderComponent
          sourceData={posCatalogData.catalogFileInfo}
          filters={filters}
          displayOptions={posCatalogData.catalogFileInfo.displayOptions}
          showLogin={setShowLoginView}
        />
        )}
        <div className="appmain flex-grow-1">
          {renderPage()}
        </div>
        {renderPopUps()}
        {posCatalogData && posCatalogData.catalogFileInfo && (
        <FooterComponent
          sourceData={posCatalogData.catalogFileInfo}
          changeLanguage={changeLanguage}
          changeTheme={changeTheme}
        />
        )}
      </div>
    </div>
  );
}
